<script setup lang="ts">
import {
  GEO_HUB_MIN_CITY,
  GEO_HUB_MIN_CITY_CATEGORY,
  isOrgIndexable,
} from "#shared/utils/geoHubThresholds";
import {
  departmentByCode,
  departmentInLabel,
} from "#shared/utils/frenchGeo";
import { nomDepuisCleVille } from "#shared/utils/villeAnnuaire";
import {
  formatCurrency,
  libelleAssociations,
  slugTheme,
  themeDepuisCategorie,
} from "@donoor/utils";
import { formatDateAssociation, fuseauDe } from "~/utils/heureAssociation";

const { t } = useI18n({ useScope: "local" });

/**
 * Hub ville de l'annuaire : /associations/ville/[ville].
 *
 * ─── Pourquoi cette page est LE levier SEO (audit 2026-08-29) ───────────
 * HelloAsso tient son référencement par ses hubs (`/annuaire`, `/agenda/
 * {ville}/{catégorie}`) : des milliers de pages qui listent des dizaines
 * d'associations chacune. Sa page association, elle, ne propose jamais
 * d'autres associations — et la nôtre non plus (règle Karim : focus total sur
 * l'asso, jamais de voisines). Tout le maillage inter-associations doit donc
 * vivre ICI, dans les hubs. Or ce hub ne listait que les associations :
 * Cergy en affichait une seule, sans cagnotte, sans adhésion, sans lien vers
 * les hubs catégorie, sans ItemList.
 *
 * Désormais la page rassemble TOUT ce que la ville a de public sur Donoor —
 * associations, cagnottes, événements, adhésions, boutiques — avec un ItemList
 * JSON-LD, les hubs catégorie de la ville, et le maillage vers les hubs
 * voisins. Une page ville « vide » ne l'est plus dès qu'une seule association
 * y a une cagnotte ou un événement.
 *
 * ─── Clé de ville (lot 4, 2026-09-27) ────────────────────────────────────
 * `{ville}` est la clé de l'annuaire : « saint-denis » (métropole),
 * « saint-denis-974 » (La Réunion), « bruxelles-be » (étranger). Les homonymes
 * métropolitains (Saint-Denis 93 et 11) partagent la page, regroupés par
 * département. Une ANCIENNE clé (« fort-de-france », « barp ») répond 301
 * vers la nouvelle (« fort-de-france-972 », « le-barp »).
 */
const config = useRuntimeConfig();
const assetsUrl = `${config.public.apiBaseUrl}/api/assets`;

const route = useRoute();
const villeSlug = (route.params as Record<string, string | string[] | undefined>).ville as string;

interface CityOrg {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  cover_image: string | null;
  description: string | null;
  objet_social: string | null;
  category: string | null;
  full_address: string | null;
  activities_count: number;
  city: string | null;
  city_slug?: string | null;
  /** Département de l'association (« 93 », « 974 »…) : regroupe les homonymes. */
  departement?: string | null;
}
interface CityCampaign {
  id: number;
  title: string;
  slug: string;
  image: string | null;
  raised: number;
  percent: number;
  organization_name: string;
  organization_logo: string | null;
}
interface CityEvent {
  id: number;
  title: string;
  slug: string;
  cover_image: string | null;
  start_date: string | null;
  location_name: string | null;
  organization_name: string;
}
interface CityMembership {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  price: number;
  organization_name: string;
}
interface CityShop {
  id: string;
  title: string;
  slug: string;
  cover_image?: string | null;
  organization_name?: string | null;
  products?: Array<{ id: string; name: string }>;
}

// Un seul useAsyncData : les 5 sources partent en parallèle côté serveur, la
// page est rendue en un passage. Chaque source dégrade vers une liste vide —
// une API en panne ne doit jamais faire tomber le hub entier.
const { data, pending } = await useAsyncData(
  `city-hub-${villeSlug}`,
  async () => {
    const safe = <T,>(p: Promise<T>, fallback: T) => p.catch(() => fallback);
    const [orgs, campaigns, events, memberships, shops] = await Promise.all([
      safe(
        $fetch<{
          cities: Array<{ slug: string; name: string; count: number }>;
          organizations: CityOrg[];
          redirection?: string | null;
        }>(`/api/organizations/by-city?city=${villeSlug}`),
        { cities: [], organizations: [] as CityOrg[], redirection: null },
      ),
      safe($fetch<{ campaigns: CityCampaign[] }>(`/api/campaigns/by-city?city=${villeSlug}`), { campaigns: [] as CityCampaign[] }),
      safe($fetch<{ events: CityEvent[] }>(`/api/events/by-city?city=${villeSlug}`), { events: [] as CityEvent[] }),
      safe($fetch<{ memberships: CityMembership[] }>(`/api/memberships/by-city?city=${villeSlug}`), { memberships: [] as CityMembership[] }),
      safe($fetch<{ shops: CityShop[] }>(`/api/shops/by-city?city=${villeSlug}`), { shops: [] as CityShop[] }),
    ]);
    return {
      redirection: orgs.redirection ?? null,
      cities: orgs.cities,
      organizations: orgs.organizations,
      campaigns: campaigns.campaigns,
      events: events.events,
      memberships: memberships.memberships,
      shops: shops.shops,
    };
  },
  { server: true, lazy: false },
);

// Ancienne clé de ville (suffixe d'outre-mer ajouté, commune renommée par la
// BAN) : 301 vers la nouvelle — le référencement acquis suit. Jamais de
// règle glob dans nuxt.config (incident du 06/08).
if (data.value?.redirection) {
  await navigateTo(`/associations/ville/${data.value.redirection}`, { redirectCode: 301 });
}

const organizations = computed(() => data.value?.organizations || []);
const campaigns = computed(() => data.value?.campaigns || []);
const events = computed(() => data.value?.events || []);
const memberships = computed(() => data.value?.memberships || []);
const shops = computed(() => data.value?.shops || []);
const otherCities = computed(() =>
  (data.value?.cities || []).filter((c) => c.slug !== villeSlug).slice(0, 30),
);

const cityName = computed(() => {
  const match = data.value?.cities?.find((c) => c.slug === villeSlug);
  return match?.name || organizations.value[0]?.city || nomDepuisCleVille(villeSlug);
});

// Homonymes métropolitains (Saint-Denis 93 et Saint-Denis 11) : même clé,
// donc même page — regroupés par département, avec un intertitre chacun.
const groupesDepartement = computed(() => {
  const groupes = new Map<string, CityOrg[]>();
  for (const org of organizations.value) {
    const cle = org.departement ?? "";
    const liste = groupes.get(cle);
    if (liste) liste.push(org);
    else groupes.set(cle, [org]);
  }
  return [...groupes.entries()].map(([code, orgs]) => {
    const d = departmentByCode(code);
    return { code, titre: d ? t('groupes.titre', { nom: d.name, code: d.code }) : t('groupes.autres'), organisations: orgs };
  });
});

// Hubs ville × thème réellement peuplés dans cette ville : on ne lie que
// des pages qui existent et ont du contenu (même règle que
// [ville]/[categorie].vue — un lien vers un hub vide serait un lien vers une
// page noindex). Thèmes communs (2026-09-26) ; « Autre » n'a pas de hub.
const categoryHubs = computed(() => {
  const counts = new Map<string, number>();
  for (const org of organizations.value) {
    const theme = themeDepuisCategorie(org.category);
    if (theme === "autre") continue;
    counts.set(theme, (counts.get(theme) || 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, count]) => count >= GEO_HUB_MIN_CITY_CATEGORY)
    .map(([theme, count]) => ({ slug: slugTheme(theme), label: libelleAssociations(theme), count }))
    .sort((a, b) => b.count - a.count);
});

// ── SEO ──
const canonicalUrl = `https://donoor.org/associations/ville/${villeSlug}`;
const totalPublic = computed(
  () => campaigns.value.length + events.value.length + memberships.value.length + shops.value.length,
);
// Pas de suffixe « | Donoor » (Karim 2026-07-16 : les gens ne connaissent pas
// la marque — le titre cible la recherche « associations à {ville} »).
const seoTitle = computed(() => t('seo.titre', { ville: cityName.value }));
const seoDescription = computed(() => {
  const n = organizations.value.length;
  const parts: string[] = [];
  if (campaigns.value.length) parts.push(t('seo.cagnottes', { n: campaigns.value.length, s: campaigns.value.length > 1 ? "s" : "" }));
  if (events.value.length) parts.push(t('seo.evenements', { n: events.value.length, s: events.value.length > 1 ? "s" : "" }));
  if (memberships.value.length) parts.push(t('seo.adhesions', { n: memberships.value.length, s: memberships.value.length > 1 ? "s" : "" }));
  if (shops.value.length) parts.push(t('seo.boutiques', { n: shops.value.length, s: shops.value.length > 1 ? "s" : "" }));
  const extras = parts.length ? t('seo.extras', { liste: parts.join(", ") }) : "";
  return t('seo.description', { n, s: n > 1 ? "s" : "", ville: cityName.value, extras });
});

// Indexabilité : au moins une asso elle-même digne d'index (cf.
// geoHubThresholds), OU du contenu public réel (une cagnotte suffit — une
// ville avec une seule asso mais une collecte active a bien un fait propre).
const indexableOrgCount = computed(() => organizations.value.filter((o) => isOrgIndexable(o)).length);
const shouldNoindex = computed(
  () => !pending.value && indexableOrgCount.value < GEO_HUB_MIN_CITY && totalPublic.value === 0,
);

// Département de rattachement (colonne `address_department`, à défaut
// l'analyse du texte — cf. villeAnnuaire). Sert le maillage REMONTANT ville →
// département → région. Homonymes de plusieurs départements : pas de parent
// unique, donc pas de lien remontant (chaque intertitre porte le sien).
const department = computed(() => {
  const codes = new Set(organizations.value.map((o) => o.departement).filter(Boolean));
  return codes.size === 1 ? departmentByCode([...codes][0]) : null;
});
const robotsContent = useRobotsContent(shouldNoindex);

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogImage: () => `https://donoor.org/api/og/recherche?type=associations&count=${organizations.value.length}`,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogUrl: canonicalUrl,
  ogLocale: "fr_FR",
  twitterCard: "summary_large_image",
  robots: () => robotsContent.value,
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
  script: computed(() => [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoTitle.value,
        description: seoDescription.value,
        url: canonicalUrl,
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Donoor", item: "https://donoor.org" },
            { "@type": "ListItem", position: 2, name: t('breadcrumb.associations'), item: "https://donoor.org/recherche/associations" },
            ...(department.value
              ? [{
                  "@type": "ListItem",
                  position: 3,
                  name: department.value.name,
                  item: `https://donoor.org/associations/departement/${department.value.slug}`,
                }]
              : []),
            { "@type": "ListItem", position: department.value ? 4 : 3, name: cityName.value, item: canonicalUrl },
          ],
        },
        // ItemList : c'est ce qui fait de la page une LISTE aux yeux des
        // moteurs (et des LLM), pas une page quelconque qui contient des liens.
        ...(organizations.value.length > 0 && {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: organizations.value.length,
            itemListElement: organizations.value.slice(0, 50).map((o, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: { "@type": "NGO", name: o.name, url: `https://donoor.org/associations/${o.slug}` },
            })),
          },
        }),
      }),
    },
  ]),
});

const img = (fileId: string | null | undefined, w = 600, h = 400) =>
  fileId ? `${assetsUrl}/${fileId}?width=${w}&height=${h}&fit=cover&format=webp&quality=80` : null;

// Jour de l'événement dans le fuseau de l'ASSOCIATION (`event.timezone`,
// Paris par défaut) ; chaîne naïve de la base = UTC (lot 2, 2026-09-25).
const formatEventDate = (iso: string | null, fuseau?: string | null) =>
  formatDateAssociation(iso, fuseau, { day: "numeric", month: "long", year: "numeric" }) || null;
</script>

<template>
  <div class="min-h-screen bg-white">
    <RechercheUnifiedHero
      active-type="associations"
      :h1="t('hero.h1', { ville: cityName })"
      :intro="t('hero.intro', { n: organizations.length, s: organizations.length > 1 ? 's' : '', p: organizations.length > 1 ? 's' : '', ville: cityName })"
      :ville-slug="villeSlug"
      :ville-name="cityName"
    />

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
      <!-- Loading -->
      <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        <div v-for="i in 4" :key="i" class="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
          <div class="h-20 sm:h-24 bg-gray-200" />
          <div class="flex flex-col items-center px-4 pb-5 -mt-7">
            <div class="w-14 h-14 rounded-xl bg-gray-300 border-3 border-white" />
            <div class="h-4 w-24 bg-gray-200 rounded mt-3" />
            <div class="h-3 w-16 bg-gray-100 rounded mt-1.5" />
          </div>
        </div>
      </div>

      <template v-else>
        <!-- Associations (homonymes de plusieurs départements : un groupe chacun) -->
        <div v-if="organizations.length > 0 && groupesDepartement.length > 1" class="space-y-10">
          <section v-for="groupe in groupesDepartement" :key="groupe.code">
            <h2 class="text-lg font-bold text-gray-900 mb-4">{{ t('groupes.section', { ville: cityName, titre: groupe.titre }) }}</h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <RechercheOrganizationCard v-for="(org, i) in groupe.organisations" :key="org.id" :org="org" :priority="i < 3" />
            </div>
          </section>
        </div>
        <div v-else-if="organizations.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <RechercheOrganizationCard v-for="(org, i) in organizations" :key="org.id" :org="org" :priority="i < 3" />
        </div>
        <div v-else class="text-center py-16">
          <UIcon name="i-lucide-building-2" class="size-12 text-gray-300 mx-auto mb-4" />
          <p class="text-gray-500 mb-4">{{ t('vide.aucune', { ville: cityName }) }}</p>
          <NuxtLink to="/recherche/associations" class="text-sm text-(--color-main) hover:underline">
            {{ t('vide.voirToutes') }}
          </NuxtLink>
        </div>

        <!-- Hubs catégorie de la ville -->
        <div v-if="categoryHubs.length > 0" class="mt-10">
          <h2 class="text-lg font-bold text-gray-900 mb-3">{{ t('domaines.titre', { ville: cityName }) }}</h2>
          <div class="flex flex-wrap gap-2">
            <NuxtLink
              v-for="hub in categoryHubs"
              :key="hub.slug"
              :to="`/associations/${villeSlug}/${hub.slug}`"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 no-underline transition-colors"
            >
              <span>{{ hub.label }}</span>
              <span class="text-gray-400">{{ hub.count }}</span>
            </NuxtLink>
          </div>
        </div>

        <!-- Cagnottes de la ville -->
        <section v-if="campaigns.length > 0" class="mt-14 pt-10 border-t border-gray-100">
          <div class="flex items-baseline justify-between gap-4 mb-5">
            <h2 class="text-xl sm:text-2xl font-bold text-gray-900">{{ t('cagnottes.titre', { ville: cityName }) }}</h2>
            <NuxtLink :to="`/cagnottes/${villeSlug}`" class="text-sm text-(--color-main) hover:underline whitespace-nowrap">{{ t('cagnottes.toutes') }}</NuxtLink>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <NuxtLink
              v-for="c in campaigns.slice(0, 6)"
              :key="c.id"
              :to="`/c/${c.slug}`"
              :aria-label="t('cagnottes.ariaLabel', { titre: c.title, organisation: c.organization_name })"
              class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 no-underline"
            >
              <div class="relative h-40 overflow-hidden bg-gray-100">
                <img v-if="c.image" :src="img(c.image)!" :alt="t('cagnottes.alt', { titre: c.title })" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div v-else class="w-full h-full flex items-center justify-center text-gray-300"><UIcon name="i-lucide-image" class="size-10" /></div>
              </div>
              <div class="p-4">
                <span class="text-xs text-gray-500 truncate block">{{ c.organization_name }}</span>
                <h3 class="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mt-1 group-hover:text-(--color-main) transition-colors">{{ c.title }}</h3>
                <div class="mt-3">
                  <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div class="h-full bg-(--color-main) rounded-full" :style="{ width: `${Math.min(100, c.percent)}%` }" />
                  </div>
                  <div class="flex items-center justify-between mt-2 text-xs text-gray-600">
                    <span class="font-semibold text-gray-900">{{ formatCurrency(c.raised) }}</span>
                    <span>{{ Math.round(c.percent) }}%</span>
                  </div>
                </div>
              </div>
            </NuxtLink>
          </div>
        </section>

        <!-- Événements de la ville -->
        <section v-if="events.length > 0" class="mt-14 pt-10 border-t border-gray-100">
          <div class="flex items-baseline justify-between gap-4 mb-5">
            <h2 class="text-xl sm:text-2xl font-bold text-gray-900">{{ t('evenements.titre', { ville: cityName }) }}</h2>
            <NuxtLink :to="`/evenements/${villeSlug}`" class="text-sm text-(--color-main) hover:underline whitespace-nowrap">{{ t('evenements.tous') }}</NuxtLink>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <NuxtLink
              v-for="e in events.slice(0, 6)"
              :key="e.id"
              :to="`/b/${e.slug}`"
              class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 no-underline"
            >
              <div class="relative h-40 overflow-hidden bg-gray-100">
                <img v-if="e.cover_image" :src="img(e.cover_image)!" :alt="t('evenements.alt', { titre: e.title })" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div v-else class="w-full h-full flex items-center justify-center text-gray-300"><UIcon name="i-lucide-calendar" class="size-10" /></div>
              </div>
              <div class="p-4">
                <span class="text-xs text-gray-500 truncate block">{{ e.organization_name }}</span>
                <h3 class="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mt-1 group-hover:text-(--color-main) transition-colors">{{ e.title }}</h3>
                <p v-if="formatEventDate(e.start_date, fuseauDe(e))" class="mt-2 text-xs text-gray-500">{{ formatEventDate(e.start_date, fuseauDe(e)) }}<template v-if="e.location_name"> · {{ e.location_name }}</template></p>
              </div>
            </NuxtLink>
          </div>
        </section>

        <!-- Adhésions de la ville -->
        <section v-if="memberships.length > 0" class="mt-14 pt-10 border-t border-gray-100">
          <div class="flex items-baseline justify-between gap-4 mb-5">
            <h2 class="text-xl sm:text-2xl font-bold text-gray-900">{{ t('adhesions.titre', { ville: cityName }) }}</h2>
            <NuxtLink :to="`/adhesions/${villeSlug}`" class="text-sm text-(--color-main) hover:underline whitespace-nowrap">{{ t('adhesions.toutes') }}</NuxtLink>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <NuxtLink
              v-for="m in memberships.slice(0, 6)"
              :key="m.id"
              :to="`/a/${m.slug}`"
              class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 no-underline"
            >
              <div class="relative h-40 overflow-hidden bg-gray-100">
                <img v-if="m.cover_image" :src="img(m.cover_image)!" :alt="t('adhesions.alt', { titre: m.title })" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div v-else class="w-full h-full flex items-center justify-center text-gray-300"><UIcon name="i-lucide-badge-check" class="size-10" /></div>
              </div>
              <div class="p-4">
                <span class="text-xs text-gray-500 truncate block">{{ m.organization_name }}</span>
                <h3 class="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mt-1 group-hover:text-(--color-main) transition-colors">{{ m.title }}</h3>
                <p class="mt-2 text-xs text-gray-600 font-semibold">{{ m.price > 0 ? formatCurrency(m.price) : t('adhesions.gratuit') }}</p>
              </div>
            </NuxtLink>
          </div>
        </section>

        <!-- Boutiques de la ville -->
        <section v-if="shops.length > 0" class="mt-14 pt-10 border-t border-gray-100">
          <div class="flex items-baseline justify-between gap-4 mb-5">
            <h2 class="text-xl sm:text-2xl font-bold text-gray-900">{{ t('boutiques.titre', { ville: cityName }) }}</h2>
            <NuxtLink :to="`/boutiques/${villeSlug}`" class="text-sm text-(--color-main) hover:underline whitespace-nowrap">{{ t('boutiques.toutes') }}</NuxtLink>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <NuxtLink
              v-for="s in shops.slice(0, 6)"
              :key="s.id"
              :to="`/boutique/${s.slug}`"
              class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 no-underline"
            >
              <div class="relative h-40 overflow-hidden bg-gray-100">
                <img v-if="s.cover_image" :src="img(s.cover_image)!" :alt="t('boutiques.alt', { titre: s.title })" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div v-else class="w-full h-full flex items-center justify-center text-gray-300"><UIcon name="i-lucide-shopping-bag" class="size-10" /></div>
              </div>
              <div class="p-4">
                <span v-if="s.organization_name" class="text-xs text-gray-500 truncate block">{{ s.organization_name }}</span>
                <h3 class="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mt-1 group-hover:text-(--color-main) transition-colors">{{ s.title }}</h3>
                <p v-if="s.products?.length" class="mt-2 text-xs text-gray-500">{{ t('boutiques.produits', { n: s.products.length, s: s.products.length > 1 ? "s" : "" }) }}</p>
              </div>
            </NuxtLink>
          </div>
        </section>

        <!-- Autres villes -->
        <div v-if="otherCities.length > 0" class="mt-16 pt-12 border-t border-gray-100">
          <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{{ t('autresVilles.titre') }}</h2>
          <p class="text-gray-500 text-sm mb-6">{{ t('autresVilles.description') }}</p>
          <div class="flex flex-wrap gap-2">
            <NuxtLink
              v-for="city in otherCities"
              :key="city.slug"
              :to="`/associations/ville/${city.slug}`"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 no-underline transition-colors"
            >
              <span>{{ city.name }}</span>
              <span class="text-gray-400">{{ city.count }}</span>
            </NuxtLink>
          </div>
        </div>

        <!-- Maillage remontant + voisins -->
        <p class="mt-12 pt-6 border-t border-gray-100 text-sm text-gray-500 leading-relaxed">
          <span class="font-medium text-gray-700">{{ t('maillage.voirAussi', { ville: cityName }) }}</span>
          <NuxtLink :to="`/cagnottes/${villeSlug}`" class="text-(--color-main) hover:underline ml-1">{{ t('maillage.cagnottes') }}</NuxtLink>
          <span class="text-gray-400 mx-1.5">·</span>
          <NuxtLink :to="`/evenements/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.evenements') }}</NuxtLink>
          <span class="text-gray-400 mx-1.5">·</span>
          <NuxtLink :to="`/adhesions/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.adhesions') }}</NuxtLink>
          <span class="text-gray-400 mx-1.5">·</span>
          <NuxtLink :to="`/boutiques/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.boutiques') }}</NuxtLink>
          <template v-if="department">
            <span class="text-gray-400 mx-1.5">·</span>
            <NuxtLink :to="`/associations/departement/${department.slug}`" class="text-(--color-main) hover:underline">
              {{ t('maillage.toutesAssociations', { departement: departmentInLabel(department) }) }}
            </NuxtLink>
          </template>
        </p>
      </template>
      </UContainer>
    </div>
  </div>
</template>

<i18n lang="json" src="../../../../i18n/pages/pages/associations/ville/[ville].json"></i18n>
